/**
 * Product database for the Tank Mix Calculator (/tools/tank-mix-calculator).
 *
 * Rates are homeowner spot/blanket rates from public product labels, expressed
 * in teaspoons per gallon of water (1 fl oz = 6 tsp). Blanket rates assume the
 * standard 1 gallon of finished mix per 1,000 sq ft carrier volume.
 *
 * Rates here are starting points for common lawn uses. The tool always tells
 * the user to verify against their label — the label is the law.
 */

import { amazonUrl } from '@/lib/affiliate'

export type SurfactantType = 'nis' | 'mso' | 'none'

export interface TankMixProduct {
  id: string
  name: string
  activeIngredient: string
  /** tsp of product per gallon of water for spot spraying */
  spotTspPerGal: number
  /** tsp of product per gallon for blanket apps (1 gal mix per 1,000 sq ft) */
  blanketTspPerGal: number
  surfactant: SurfactantType
  /** Grass-safety caution shown with every recipe */
  caution: string
  /** Direct or legacy Amazon affiliate URL; omit when no verified listing */
  affiliateUrl?: string
  /** PAAPI product image (m.media-amazon.com) */
  imageUrl?: string
}

export interface SurfactantInfo {
  type: SurfactantType
  name: string
  /** tsp of surfactant per gallon of finished mix */
  tspPerGal: number
  /** Brand-name product shown on the "What you'll need" pick card */
  pickName: string
  affiliateUrl?: string
  imageUrl?: string
}

export const TANK_MIX_SURFACTANTS: Record<Exclude<SurfactantType, 'none'>, SurfactantInfo> = {
  nis: {
    type: 'nis',
    name: 'Non-ionic surfactant (NIS)',
    tspPerGal: 1.5, // 0.25 fl oz per gallon
    pickName: 'Southern Ag Non-Ionic Surfactant',
    // PAAPI-verified 2026-07-07: "Southern Ag Surfactant for Herbicides Non-Ionic"
    affiliateUrl: amazonUrl('B004XDHRCE'),
    imageUrl: 'https://m.media-amazon.com/images/I/51fBawyDg7L._SL500_.jpg',
  },
  mso: {
    type: 'mso',
    name: 'Methylated seed oil (MSO)',
    tspPerGal: 6, // 1 fl oz per gallon
    pickName: 'Southern Ag Methylated Seed Oil (MSO)',
    // PAAPI-verified 2026-07-07: "Southern Ag Methylated Seed Oil (MSO) Surfactant"
    affiliateUrl: amazonUrl('B0149IA526'),
    imageUrl: 'https://m.media-amazon.com/images/I/415HuWZ3ajL._SL500_.jpg',
  },
}

export const TANK_MIX_PRODUCTS: Record<string, TankMixProduct> = {
  tenacity: {
    id: 'tenacity',
    name: 'Tenacity',
    activeIngredient: 'Mesotrione 40%',
    spotTspPerGal: 1,
    blanketTspPerGal: 0.5,
    surfactant: 'nis',
    caution: 'Do not use on Bermuda, Zoysia, or bentgrass lawns. Expect temporary whitening of treated weeds and some turf.',
    // PAAPI-verified 2026-07-07: "Syngenta Tenacity Herbicide"
    affiliateUrl: amazonUrl('B005DUTNF0'),
    imageUrl: 'https://m.media-amazon.com/images/I/41tEE7n3sRL._SL500_.jpg',
  },
  quinclorac: {
    id: 'quinclorac',
    name: 'Quinclorac 75DF',
    activeIngredient: 'Quinclorac 75%',
    spotTspPerGal: 1.5,
    blanketTspPerGal: 1.5,
    surfactant: 'mso',
    caution: 'Injures St. Augustine, centipede, and bahia lawns. Safe on Bermuda, Zoysia, and most cool-season turf.',
    // PAAPI-verified 2026-07-17: "Quali-Pro Quinclorac 75 DF Selective
    // Herbicide 1lb" — the dry 75DF formulation this entry's rates assume.
    // Buy-box OOS on PAAPI but third-party sellers convert; keep the link.
    affiliateUrl: amazonUrl('B006LAVM3W'),
  },
  'two-four-d': {
    id: 'two-four-d',
    name: '2,4-D Amine',
    activeIngredient: '2,4-D 46.8%',
    spotTspPerGal: 5,
    blanketTspPerGal: 3,
    surfactant: 'nis',
    caution: 'Do not apply above 85°F or to newly seeded turf. Spot-treat only on St. Augustine and centipede.',
    // PAAPI-verified 2026-07-07: "Southern Ag Amine 2,4-D WEED KILLER, 32oz"
    affiliateUrl: amazonUrl('B0072289CA'),
    imageUrl: 'https://m.media-amazon.com/images/I/5100xBWL0FL._SL500_.jpg',
  },
  glyphosate: {
    id: 'glyphosate',
    name: 'Glyphosate 41%',
    activeIngredient: 'Glyphosate 41%',
    spotTspPerGal: 15,
    blanketTspPerGal: 15,
    surfactant: 'none',
    caution: 'Non-selective. Kills any grass it touches — spot use and renovations only.',
    // PAAPI-verified 2026-07-07: "Compare-N-Save Concentrate Glyphosate
    // Concentrate Grass & Weed Killer Herbicide, 32-Ounce" (41% glyphosate)
    affiliateUrl: amazonUrl('B00ARKS23A'),
    imageUrl: 'https://m.media-amazon.com/images/I/41HuJN+ZfcL._SL500_.jpg',
  },
  triclopyr: {
    id: 'triclopyr',
    name: 'Triclopyr 4',
    activeIngredient: 'Triclopyr 61.6%',
    spotTspPerGal: 2.25,
    blanketTspPerGal: 1.5,
    surfactant: 'nis',
    caution: 'Do not use on Bermuda. Best on cool-season lawns for violets, clover, and creeping weeds.',
    // PAAPI-verified 2026-07-07: "ALLIGARE - Triclopyr 4 Herbicide ... 1 Quart"
    affiliateUrl: amazonUrl('B00M8C4YNM'),
    imageUrl: 'https://m.media-amazon.com/images/I/41pi9ZUo-hL._SL500_.jpg',
  },
  celsius: {
    id: 'celsius',
    name: 'Celsius WG',
    activeIngredient: 'Dicamba + thiencarbazone',
    spotTspPerGal: 0.5,
    blanketTspPerGal: 0.33,
    surfactant: 'nis',
    caution: 'Warm-season lawns only (Bermuda, Zoysia, St. Augustine, centipede). Skip cool-season turf.',
    // PAAPI-verified 2026-07-07: "Celsius WG - 0.226 oz Easy Mix Packet"
    affiliateUrl: amazonUrl('B0BTN1DPMD'),
    imageUrl: 'https://m.media-amazon.com/images/I/41hwVWk83-L._SL500_.jpg',
  },
}

export const TANK_MIX_PRODUCT_IDS = Object.keys(TANK_MIX_PRODUCTS)

export const TANK_SIZES = [
  { gallons: 1, label: '1 gal', description: 'hand sprayer' },
  { gallons: 2, label: '2 gal', description: 'pump sprayer' },
  { gallons: 4, label: '4 gal', description: 'backpack' },
] as const
